import React, { useContext, useEffect, useMemo, useState } from 'react';
import debounce from 'lodash/debounce';
import { createManageDomainBreadcrumbs } from '../Utils/BreadcrumbUtils';
import { Breadcrumb } from '../Components/Breadcrumb';
import { CREATE_DOMAIN, CREATE_DOMAIN_PERMISSION_MESSAGE, CREATE_PERMISSION, DELETE_PERMISSION, READ_PERMISSION, UPDATE_DOMAIN_PERMISSION_MESSAGE, UPDATE_PARTY_PERMISSION_MESSAGE, UPDATE_PERMISSION } from '../Utils/Constants';
import DomainTable from './DomainTable';
import { CreateDomainDialog } from './CreateDomainDialog';
import { useNavigate } from "react-router";
import DetailSection from './DetailSection';
import { TParty } from '../Types/party.types';
import { TCreateDomainPayload, TIndustry, TIndustryUpdatePayload } from '../Types/domain.types';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import { CreatePartyDialog } from '../Party/CreatePartyDialog';
import Button from 'Components/DesignSystem/Button/Button';
import Typography from 'Components/DesignSystem/Typography/Typography';
import Search from 'Components/DesignSystem/Search/Search';
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';
import Pagination from 'Components/DesignSystem/Pagination/Pagination';
import EmptyState from 'Components/DesignSystem/EmptyState/EmptyState';
import NoDataIllustration from 'Components/DesignSystem/EmptyState/NoDataIllustration';
import PageSkeleton from 'Components/DesignSystem/PageSkeleton/PageSkeleton';
import { TPaginationParams } from 'Pages/GMS/Performance/analytics';
import { AppContext } from 'Context/AppContext';
import AppSettings from 'Settings/AppSettings';
import ServiceWrapper from 'Services/ServiceWrapper';
import AuthManager from 'Common/AuthManager';

/** The two page sizes the legacy `<select>` offered, held to exactly those (§ 118). */
const PAGE_SIZE_OPTIONS = [
    { value: 10, label: '10' },
    { value: 20, label: '20' },
];

/** `CustomSearchBar`'s own default (`props.delay ?? 600`), preserved exactly. */
const SEARCH_DEBOUNCE_MS = 600;

type TDomainType = {
    partyId?: string;
}

/**
 * Domain management (`/admin/party/domain/:id`) on the design system (§ 119).
 *
 * `CustomSearchBar`, `PaginatorWithApi`, `NoDataComponent` and the raw
 * `button-style` / `<select>` pairs are gone from this screen; `Breadcrumb` and
 * `DetailSection` were migrated in place, so the **Channel screen inherits both**.
 *
 * Two things here are load-bearing and easy to break:
 *
 * - **`handleSearchChange` sets `isDomainsLoading` true.** That is not bookkeeping — it
 *   is the fix for the 2026-08-11 bug (`okf/admin/e2e.md` § 5.2). The create-first empty
 *   state is gated on `!isDomainsLoading && domains.length === 0 && searchTerm === ''`,
 *   and the fetch lives in `DomainTable`'s own effect. Clear a zero-result search and,
 *   without this flag, the parent would swap to the create-first branch, **unmount the
 *   table**, and there would be nothing left to refetch — the page sticks with no way
 *   back short of a reload.
 * - **The Create Domain button exists in two branches** (create-first empty state and
 *   list toolbar). They now share ONE testid, `domain-create-btn`, because only one is
 *   ever mounted. Before, they were `party-create-domain-btn` and
 *   `domain-managedomain-button-1` — a spec had to know which state the page was in
 *   before it could click the same action.
 *
 * **Behaviour deliberately unchanged** (presentational scope): without
 * `gmsadministration_read` the effect redirects and returns *before*
 * `setIsLoading(false)`, so the loading skeleton runs forever behind the redirect;
 * neither dialog validates a required field; and `Create Domain` is never `disabled`
 * without create permission — it refuses with a toast.
 */
const ManageDomain = ({ partyId }: TDomainType) => {
    const navigate = useNavigate();
    const appContext = useContext(AppContext);
    const api_base_url = AppSettings.GMS_BACKEND_API_URL;

    const readPermission = appContext.pm.checkPermission(READ_PERMISSION);
    const createPermission = appContext.pm.checkPermission(CREATE_PERMISSION);
    const updatePermission = appContext.pm.checkPermission(UPDATE_PERMISSION);
    const deletePermission = appContext.pm.checkPermission(DELETE_PERMISSION);

    const [partyData, setPartyData] = useState<TParty | null>(null);
    const [domains, setDomains] = useState<TIndustry[]>([]);

    const [isCreateDomainOpen, setIsCreateDomainOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    
    const [isEditPartyOpen, setIsEditPartyOpen] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [showLoader, setShowLoader] = useState<boolean>(false);
    const [isCreatingDomain, setIsCreatingDomain] = useState(false);
    const [isDomainsLoading, setIsDomainsLoading] = useState(true);

    const [paginationParams, setPaginationParams] = useState<TPaginationParams>({pageNumber: 1, pageSize: 10});
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [searchTerm, setSearchTerm] = useState<string>('');
    /* The controlled input's own value. `searchTerm` is the debounced one the child's
       fetch effect keys on — `CustomSearchBar` held this half internally. */
    const [searchInput, setSearchInput] = useState<string>('');
    const [refreshDomainTable, setRefreshDomainTable] = useState(0);
    
    const [editDomainDialog, setEditDomainDialog] = useState<{
        isOpen: boolean;
        domain: TIndustry | null;
    }>({ isOpen: false, domain: null });
    const [isUpdatingDomain, setIsUpdatingDomain] = useState(false);

    useEffect(() => {
        const fetchPartyData = async () => {
            if (!partyId) {
                setIsLoading(false);
                return;
            }

            try {
                setIsLoading(true);
                const partyDataResponse = await ServiceWrapper.doGet( `${api_base_url}user/party/list/${partyId}`, null, null, AuthManager.getGmsToken(),); 
                const response = partyDataResponse?.data;
                if (response && response.done && response.data && response.data.length > 0) {
                    setPartyData(response.data[0]);
                } else {
                    console.error('No party data found');
                    toast.error('Failed to load party details');
                }
            } catch (error) {
                console.error('Error fetching party data:', error);
                toast.error('Error loading party details');
            } finally {
                setIsLoading(false);
            }
        };

        if (!readPermission) {
            navigate("/workspace/module/permission/denied");
            return;
        } else {
            fetchPartyData();
        }
    }, [partyId]);

    useEffect(() => {
        if (refreshDomainTable > 0) {
            setIsDomainsLoading(true);
        }
    }, [refreshDomainTable]);

    const handleCreateDomain = async (formData: any) => {
        if (!createPermission) {
            toast.warning(CREATE_DOMAIN_PERMISSION_MESSAGE);
            return;
        }    
        if (!partyId) return;
        
        setIsCreatingDomain(true);
        
        try {
            const payload: TCreateDomainPayload = {
                name: formData.domainName,
                description: formData.description || '',
                active: true
            };
            
            const domainResponse = await ServiceWrapper.doPost( 
                `${api_base_url}party/${partyId}/industry`, 
                payload, 
                null, 
                AuthManager.getGmsToken()
            );
            const response = domainResponse?.data;
            if (response && response.done) {
                toast.success('Domain created successfully!');
                setIsCreateDomainOpen(false);
                
                setRefreshDomainTable(prev => prev + 1);
                
                setPaginationParams(prev => ({ ...prev, pageNumber: 1 }));
                await appContext.refreshHierarchy.refreshParties();
            } else {
                toast.error(response?.message || 'Failed to create domain');
            }
        } catch (error) {
            console.error('Error creating domain:', error);
            toast.error('An error occurred while creating the domain');
        } finally {
            setIsCreatingDomain(false);
        }
    };

    const handleUpdateDomain = async (formData: any) => {
        if (!updatePermission) {
            toast.warning(UPDATE_DOMAIN_PERMISSION_MESSAGE);
            return;
        }
        
        const domain = editDomainDialog.domain;
        if (!domain) return;
        
        setIsUpdatingDomain(true);
        
        try {
            const payload: TIndustryUpdatePayload = {
                active: true,
                description: formData.description || '',
                id: domain.id,
                name: formData.domainName,
                sams_id: domain.sams_id
            };
            
            const domainResponse = await ServiceWrapper.doPost( 
                `${api_base_url}industry/edit/${domain.id}`, 
                payload, 
                null, 
                AuthManager.getGmsToken()
            );
            const response = domainResponse?.data;
            
            if (response && response.done) {
                toast.success('Domain updated successfully!');
                setEditDomainDialog({ isOpen: false, domain: null });
                
                setRefreshDomainTable(prev => prev + 1);
                
                await appContext.refreshHierarchy.refreshParties();
            } else {
                toast.error(response?.message || 'Failed to update domain');
            }
        } catch (error) {
            console.error('Error updating domain:', error);
            toast.error('An error occurred while updating the domain');
        } finally {
            setIsUpdatingDomain(false);
        }
    };

    const handleEditDomain = (domain: TIndustry, index: number) => {
        if (!updatePermission) {
            toast.warning(UPDATE_DOMAIN_PERMISSION_MESSAGE);
            return;
        }
        setEditDomainDialog({
            isOpen: true,
            domain: domain
        });
    };

    const handleNavigateToChannel = (domain: TIndustry) => {
        navigate(`/admin/party/${partyId ?? ''}/domain/${domain?.id ?? ''}/channel`);
    };

    const handleNavigateToParties = () => {
        navigate('/admin/party/manage');
    };

    const breadcrumbItems = partyData 
        ? createManageDomainBreadcrumbs(partyData.name, handleNavigateToParties)
        : [];

    const handleEditParty = () => {
        if (!updatePermission) {
            toast.warning(UPDATE_PARTY_PERMISSION_MESSAGE);
            return;
        }  
        setIsEditPartyOpen(true);
    };

    const handleUpdateParty = async (formData: any) => {
        if (!updatePermission) {
            toast.warning(UPDATE_PARTY_PERMISSION_MESSAGE);
            return;
        } 
        if (!partyData || !partyId) return;
        
        setIsUpdating(true);
        
        try {
            const partyResponse = await ServiceWrapper.doPost( 
                `${api_base_url}party/edit/${partyId}`, 
                formData, 
                null, 
                AuthManager.getGmsToken()
            );
            const response = partyResponse?.data;
            if (response && response.done) {
                toast.success('Party updated successfully!');
                
                setPartyData({
                    ...partyData,
                    name: formData.name,
                    address: formData.address,
                    party_type: formData.party_type
                });
                
                setIsEditPartyOpen(false);
                await appContext.refreshHierarchy.refreshParties();
            } else {
                toast.error(response?.message || 'Failed to update party');
            }
        } catch (error) {
            console.error('Error updating party:', error);
            toast.error('An error occurred while updating the party');
        } finally {
            setIsUpdating(false);
        }
    };

    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
        setPaginationParams(prev => ({ ...prev, pageNumber: 1 }));
        // Keep DomainTable mounted while the searched refetch runs: the fetch lives in
        // DomainTable's useEffect, so if the empty-state branch unmounted it on a cleared
        // search (searchTerm '' + stale empty domains), nothing could ever refetch.
        setIsDomainsLoading(true);
    };

    /* `CustomSearchBar` built its debounce as `useCallback(debounce(...), [])`. Same
       shape, same 600ms, now cancelled on unmount — a pending trailing call used to
       land after the page was gone. */
    const debouncedSearch = useMemo(
        () => debounce(handleSearchChange, SEARCH_DEBOUNCE_MS),
        []
    );

    useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

    const onSearchInputChange = (value: string) => {
        /* `trimStart()` was `CustomSearchBar`'s, applied to the visible value and the
           debounced one alike. */
        const next = value.trimStart();
        setSearchInput(next);
        debouncedSearch(next);
    };

    const handlePageChange = (pageNumber: number) => {
        setPaginationParams((prevState: TPaginationParams) => ({
            ...prevState,
            pageNumber: pageNumber,
        }));
    };
        
    const updatePageSize = (pageSize: number) => {
        setPaginationParams((prevState: TPaginationParams) => ({
            ...prevState,
            pageSize: pageSize,
            pageNumber: 1
        }));
    };

    if (isLoading) {
        /*
          This replaces the whole page body — breadcrumb, card, toolbar and table — so it
          takes `PageSkeleton`, not a centred `Spinner` (the § 117 placement rule).

          `summary-list`, NOT `detail`. It was `detail` first, and that preset draws a
          title, two top-right buttons and five stacked form fields inside a `max-w-xl`
          column — an AddEdit form. None of it arrives here, so every block moved when
          the data landed, which is the one thing a skeleton exists to prevent. The
          preset was added for this shape rather than approximated with the nearer of
          two wrong answers; Manage Channel is its second consumer when that screen
          migrates.

          The legacy loader was a bare spinning `<svg>` with the text "Loading party
          details..." and no `role` — the wait was silent as well as the wrong shape.
        */
        return <PageSkeleton preset="summary-list" label="Loading party details" data-testid="domain-page-loading" />;
    }

    if (!partyData) {
        return (
            <EmptyState
                title="Party not found"
                size="md"
                data-testid="domain-party-not-found"
            />
        );
    }

    const onCreateDomainClick = () => {
        if (!createPermission) {
            toast.warning(CREATE_DOMAIN_PERMISSION_MESSAGE);
            return;
        }
        setIsCreateDomainOpen(true);
    }

    return (
        <>
            <Breadcrumb items={breadcrumbItems} />
            <DetailSection 
                config={{
                    type: 'party',
                    data: {
                        title: partyData.name,
                        partyType: partyData.party_type || 'N/A',
                        address: partyData.address || 'N/A'
                    },
                    onEdit: handleEditParty
                }}
            />
            {/* Create-first empty state only when the party truly has no domains — a search
                with zero matches must keep the search bar mounted (so it can be cleared) and
                show DomainTable's "No domains found" row instead. */}
            {!isDomainsLoading && domains.length === 0 && searchTerm === '' ? (
                /* `EmptyState` folds three separate pieces into one: `NoDataComponent`'s
                   house artwork, its title, and the centred Create button that used to be
                   a sibling `<div>` below it. `illustration` + `action` are exactly those
                   slots (§ 64), so the arrangement is the component's job now. */
                <EmptyState
                    illustration={<NoDataIllustration />}
                    title="No Domain found"
                    size="md"
                    data-testid="domain-empty-state"
                    action={
                        <Button
                            variant="primary"
                            onClick={onCreateDomainClick}
                            data-testid="domain-create-btn"
                        >
                            {CREATE_DOMAIN}
                        </Button>
                    }
                />
            ) : (
                <>
                    {(domains.length !== 0 || searchTerm !== '') && (
                        <div className="tw-flex tw-items-center tw-justify-between tw-mt-2">
                            <div className="tw-w-[25%] tw-mt-4">
                                <Search
                                    value={searchInput}
                                    onChange={onSearchInputChange}
                                    /* § 288 — same pair as ManageChannel: the debounce
                                       gap, then the child table's fetch. */
                                    loading={showLoader || searchInput !== searchTerm}
                                    loadingLabel="Searching domains"
                                    placeholder="Search domains"
                                    ariaLabel="Search domains"
                                    size="md"
                                    data-testid="domain-search"
                                />
                            </div>
                            {/* Same testid as the empty-state button above: only one of
                                the two branches is ever mounted, so this identifies
                                exactly one element while letting a spec click "create a
                                domain" without first working out which state it is in. */}
                            <Button
                                variant="primary"
                                onClick={onCreateDomainClick}
                                className="tw-w-max tw-whitespace-nowrap"
                                data-testid="domain-create-btn"
                            >
                                {CREATE_DOMAIN}
                            </Button>
                        </div>
                    )}
                    {partyId && (
                        <DomainTable 
                            partyId={partyId}
                            searchTerm={searchTerm}
                            showLoader={showLoader}
                            domains={domains}
                            setDomains={setDomains}
                            setShowLoader={setShowLoader}
                            setTotalRecords={setTotalRecords}
                            paginationParams={paginationParams}
                            refreshTrigger={refreshDomainTable}
                            onDomainsLoaded={() => setIsDomainsLoading(false)}
                            onEditDomain={handleEditDomain}
                            onNavigateToChannel={handleNavigateToChannel}
                            updatePermission={updatePermission}
                            deletePermission={deletePermission}
                        />
                    )}
                </>
            )}
            {!showLoader && domains.length !== 0 && totalRecords > 0 ?
                <div id="pagination-container" className="tw-py-2">
                    <div className="tw-flex tw-justify-end tw-items-center tw-gap-3 tw-flex-wrap">
                        <div className="tw-flex tw-items-center tw-gap-2">
                            <Typography variant="body-primary" as="span" className="tw-text-ink-secondary">Show</Typography>
                            <Dropdown
                                label=""
                                aria-label="Records on page select"
                                size="sm"
                                className="tw-w-[84px]"
                                options={PAGE_SIZE_OPTIONS}
                                value={paginationParams?.pageSize ?? 10}
                                onChange={(value) => updatePageSize(Number(value))}
                                id="page-size-select"
                                data-testid="domain-page-size"
                            />
                        </div>
                        <Pagination
                            page={paginationParams?.pageNumber}
                            totalItems={totalRecords}
                            pageSize={paginationParams?.pageSize}
                            onPageChange={handlePageChange}
                            size="sm"
                            id="paginator"
                            data-testid="domain-pagination"
                        />
                    </div>
                </div> : null}
            
            <CreateDomainDialog 
                isOpen={isCreateDomainOpen}
                onClose={() => setIsCreateDomainOpen(false)}
                onSave={handleCreateDomain}
                isLoading={isCreatingDomain}
            />
            
            <CreateDomainDialog
                isOpen={editDomainDialog.isOpen}
                onClose={() => setEditDomainDialog({ isOpen: false, domain: null })}
                onSave={handleUpdateDomain}
                isLoading={isUpdatingDomain}
                editData={editDomainDialog.domain}
                mode="edit"
            />
            
            <CreatePartyDialog
                isOpen={isEditPartyOpen}
                onClose={() => setIsEditPartyOpen(false)}
                onSave={handleUpdateParty}
                isLoading={isUpdating}
                editData={partyData}
                mode="edit"
            />
        </>
    );
}

export default ManageDomain;